import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  User, BookOpen, GraduationCap, Calendar, MessageSquare, Search, FileText, ClipboardCheck, UserCheck, CircleAlert, CheckCircle,
  User2,
} from 'lucide-react';
import useFetch from '../constants/useFetch';
import { setStudentData } from '../redux/slices/studentDataSlice';
import { API_BASE_URL } from '../constants';
import { useNavigate } from 'react-router-dom';

const StudentDashboard = () => {
  const { user: userInfo } = useSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [isSubjectModalOpen, setIsSubjectModalOpen] = useState(false);
  const [isPsychoModalOpen, setIsPsychoModalOpen] = useState(false);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [chatRecipient, setChatRecipient] = useState('');
  const [chatMessage, setChatMessage] = useState('');
  const [selectedResult, setSelectedResult] = useState(null);
  const [commentModal, setCommentModal] = useState({
    open: false,
    role: '',
    comment: '',
    date: null,
    term: '',
    session: '',
    className: '',
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const dataUrl = `${API_BASE_URL}/api/web/student/dashboard-data`;
  const { data: dashboardData, loading: dashboardLoading, error: dashboardError, refetch: refetchDashboard } = useFetch(dataUrl, 'studentDashboardData');

  // Dispatch to Redux once fetched
  // useEffect(() => {
  //   if (dashboardData) {
  //     dispatch(setStudentData(dashboardData));
  //   }
  // }, [dashboardData, dispatch]);

  // console.log({dashboardData});
  
  // Utility function to format decimal values
const formatScore = (value) => {
  if (value === undefined || value === null || value === '') return '-';

  if (value==0 || value==0.0 || value=='0' || value=='0.0') {
    return '-'
  }
  
  const num = parseFloat(value);
  if (isNaN(num)) return '';
  
  // If the number ends with .0, show as integer
  if (num % 1 === 0) {
    return Math.floor(num);
  }
  
  // Otherwise, show as is
  return num;
};

  const showPositionInActualFormat = (position) => {
    if (position === 1) return '1st';
    if (position === 2) return '2nd';
    if (position === 3) return '3rd';
    return `${position}th`;
  };

  // Mock student stats - in real app, this would come from API
  const studentStats = {
    currentClass: dashboardData?.currentClass?.class_name || 'N/A',
    schoolPost: dashboardData?.student?.post || 'N/A',
    classPost: dashboardData?.student?.class_post || 'N/A',
    positionInClass: dashboardData?.positions?.currentTermPositionClass || 'N/A',
    positionInClassLevel: dashboardData?.positions?.currentTermPositionClassLevel || 'N/A',
    overallPositionClass: dashboardData?.positions?.overallPositionClass || 'N/A',
    overallPositionClassLevel: dashboardData?.positions?.overallPositionClassLevel || 'N/A',
  };

  const formTeacher = dashboardData?.formTeacher || {};
  // console.log({formTeacher});
  
  const results = dashboardData?.results || [];

  const filteredResults = results.filter(result => 
    result.class_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    result.session?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCheckResult = (result, term) => {
    // Go directly to result page without modal
    const selectedTermValue = term || userInfo.currentTerm;
    navigate(`/student-single-result?studentUuid=${userInfo?.uuid}&className=${result?.class_name}&session=${result?.session}&term=${selectedTermValue}`);
  };

  const openCommentModal = (termData, role, termLabel, session, className) => {
    const comment =
      role === 'form_teacher'
        ? termData?.comments?.form_teacher_comments
        : termData?.comments?.principal_comments;
    const date =
      role === 'form_teacher'
        ? termData?.comments?.form_teacher_comment_date
        : termData?.comments?.principal_comment_date;

    setCommentModal({
      open: true,
      role: role === 'form_teacher' ? 'Form Teacher' : 'Principal',
      comment: comment || 'No comment available.',
      date: date || null,
      term: termLabel,
      session,
      className,
    });
  };


  const handleSendMessage = () => {
    // Placeholder for sending message (backend integration needed)
    alert(`Message to ${chatRecipient}: ${chatMessage}`);
    setIsChatModalOpen(false);
    setChatMessage('');
    setChatRecipient('');
  };

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-green-500 to-teal-600 rounded-xl p-4 sm:p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-4">
            {/* <img
              src={userInfo?.profile_picture || 'https://via.placeholder.com/64'}
              alt="Profile"
              className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover"
            /> */}
             {/* Initials Avatar */}
            {/* <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full 
                            bg-teal-500 text-white 
                            flex items-center justify-center
                            font-bold text-lg sm:text-xl
                            uppercase select-none">
              {`${userInfo?.firstname?.[0] || ''}${userInfo?.lastname?.[0] || ''}`}
            </div> */}
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full
                bg-teal-500 text-white
                flex items-center justify-center
                select-none">
            <User2 className="w-6 h-6 sm:w-8 sm:h-8" />
          </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold">
                Welcome back, {userInfo?.firstname} {userInfo?.lastname}!
              </h1>
              <p className="text-green-100 mt-1 text-sm sm:text-base">
                {dashboardLoading ? 'Loading your dashboard...' : `Student in ${studentStats.currentClass} • ${userInfo?.currentSession} Session • ${userInfo?.currentTerm} Term`}
              </p>
            </div>
          </div>
          <div className="text-left sm:text-right">
            <p className="text-green-100 text-xs sm:text-sm">Today's Date</p>
            <p className="text-lg sm:text-xl font-semibold">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      

      {/* Student Info and Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">

        {/* Check Current Result Button */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Outer button border */}
        {dashboardLoading ? (
          <div className="rounded-xl border-2 border-orange-300 p-1">
            <button
              
              className="w-full flex items-center justify-center gap-3
                        rounded-lg px-4 py-3
                        bg-orange-500 hover:bg-orange-600
                        text-white font-semibold text-sm sm:text-base
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2
                        active:scale-[0.98]"
            >
              <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6" />
              Loading...
            </button>
          </div>
          ):(
          <div className="rounded-xl border-2 border-orange-300 p-1">
          <button
            onClick={() => {
              const currentTerm = dashboardData?.currentTerm || userInfo.currentTerm;
              const currentSession = dashboardData?.currentSession || userInfo.currentSession;
              navigate(
                `/student-single-result?studentUuid=${userInfo?.uuid}&className=${studentStats?.currentClass}&session=${currentSession}&term=${currentTerm}`
              );
            }}
            className="w-full flex items-center justify-center gap-3
                      rounded-lg px-4 py-3
                      bg-orange-500 hover:bg-orange-600
                      text-white font-semibold text-sm sm:text-base
                      transition-all duration-200
                      focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-2
                      active:scale-[0.98]"
          >
            <GraduationCap className="w-5 h-5 sm:w-6 sm:h-6" />
            Check Current Result
          </button>
          </div>
        )}
      </div>


        {/* Basic Student Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Student Info</p>
              <p className="text-sm text-gray-900 dark:text-white">Admission: {userInfo?.admission_no || 'N/A'}</p>
              <p className="text-sm text-gray-900 dark:text-white">Email: {userInfo?.student_email || 'N/A'}</p>
              <p className="text-sm text-gray-900 dark:text-white">Phone: {userInfo?.student_phone || 'N/A'}</p>
              {/* <button
                onClick={() => setIsInfoModalOpen(true)}
                className="mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                View More
              </button> */}
            </div>
            {/* <User className="w-6 h-6 sm:w-8 sm:h-8 text-blue-500 flex-shrink-0" /> */}
          </div>
        </div>

        {/* Current Class Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Current Class</p>
              <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{studentStats.currentClass}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">School Post: {studentStats.schoolPost}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Class Post: {studentStats.classPost}</p>
            </div>
            {/* <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-green-500 flex-shrink-0" /> */}
          </div>
        </div>

        {/* Form Teacher Info */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Form Teacher</p>
              <div className="flex items-center space-x-2">
                {formTeacher?.profile_picture && (<img
                  src={formTeacher?.profile_picture || 'https://via.placeholder.com/32'}
                  alt="Form Teacher"
                  className="w-8 h-8 rounded-full object-cover"
                />)}
                <div>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {formTeacher?.firstname} {formTeacher?.lastname}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{formTeacher?.email || 'N/A'}</p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">{formTeacher?.phone || 'N/A'}</p>
                </div>
              </div>
            </div>
            {/* <UserCheck className="w-6 h-6 sm:w-8 sm:h-8 text-purple-500 flex-shrink-0" /> */}
          </div>
        </div>

        {/* Performance Rankings */}
        {/* <div className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">Performance Rankings</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Current Term</p>
              <p className="text-sm text-gray-900 dark:text-white">
                In {studentStats.currentClass}: {studentStats?.positionInClass !== 'N/A' ? showPositionInActualFormat(studentStats.positionInClass) : 'N/A'}
              </p>
              <p className="text-sm text-gray-900 dark:text-white">
                In All {studentStats.currentClass?.replace(/[0-9]/g, '').trim() || 'Classes'}: {studentStats?.positionInClassLevel !== 'N/A' ? showPositionInActualFormat(studentStats.positionInClassLevel) : 'N/A'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 mb-1">Cumulative (All Terms)</p>
              <p className="text-sm text-gray-900 dark:text-white">
                In {studentStats.currentClass}: {studentStats?.overallPositionClass !== 'N/A' ? showPositionInActualFormat(studentStats.overallPositionClass) : 'N/A'}
              </p>
              <p className="text-sm text-gray-900 dark:text-white">
                In All {studentStats.currentClass?.replace(/[0-9]/g, '').trim() || 'Classes'}: {studentStats?.overallPositionClassLevel !== 'N/A' ? showPositionInActualFormat(studentStats.overallPositionClassLevel) : 'N/A'}
              </p>
            </div>
            <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500 flex-shrink-0" />
          </div>
        </div> */}

        

      </div>

      {/* Results Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">Academic Results</h2>
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search results..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full sm:w-64 pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
              />
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6">
          {dashboardLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-12 bg-gray-300 dark:bg-gray-600 rounded animate-pulse"></div>
              ))}
            </div>
          ) : filteredResults.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="py-3 px-4">S/N</th>
                    <th className="py-3 px-4">Session</th>
                    <th className="py-3 px-4">Class</th>
                    <th className="py-3 px-4">First Term</th>
                    <th className="py-3 px-4">Second Term</th>
                    <th className="py-3 px-4">Third Term</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((result, index) => (
                    <tr key={index} className="text-sm border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="py-3 px-4">{index + 1}</td>
                      <td className="py-3 px-4">{result.session}</td>
                      <td className="py-3 px-4">{result.class_name}</td>
                      <td className="py-3 px-4">
                        {result.first_term ? (
                          <>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedResult(result?.first_term);
                                  setIsSubjectModalOpen(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                Subjects
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedResult(result?.first_term);
                                  setIsPsychoModalOpen(true);
                                }}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                Psychomotor
                              </button>
                              <button
                                onClick={() => handleCheckResult(result, 'first')}
                                className="text-purple-600 hover:text-purple-800 text-xs"
                              >
                                {result.checked ? 'View Result' : 'Check Result'}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2 text-xs">
                              <button
                                onClick={() => openCommentModal(result.first_term, 'form_teacher', 'First Term', result.session, result.class_name)}
                                className="flex items-center gap-1 text-orange-600 hover:text-orange-800"
                              >
                                <MessageSquare className="w-4 h-4" /> Form Teacher Comments
                              </button>
                              <button
                                onClick={() => openCommentModal(result.first_term, 'principal', 'First Term', result.session, result.class_name)}
                                className="flex items-center gap-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                              >
                                <FileText className="w-4 h-4" /> Principal Comments
                              </button>
                            </div>
                          </>
                        ) : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        {result.second_term ? (
                          <>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedResult(result?.second_term);
                                  setIsSubjectModalOpen(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                Subjects
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedResult(result?.second_term);
                                  setIsPsychoModalOpen(true);
                                }}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                Psychomotor
                              </button>
                              <button
                                onClick={() => handleCheckResult(result, 'second')}
                                className="text-purple-600 hover:text-purple-800 text-xs"
                              >
                                {result.second_term?.checked ? 'View Result' : 'Check Result'}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2 text-xs">
                              <button
                                onClick={() => openCommentModal(result.second_term, 'form_teacher', 'Second Term', result.session, result.class_name)}
                                className="flex items-center gap-1 text-orange-600 hover:text-orange-800"
                              >
                                <MessageSquare className="w-4 h-4" /> Form Teacher Comments
                              </button>
                              <button
                                onClick={() => openCommentModal(result.second_term, 'principal', 'Second Term', result.session, result.class_name)}
                                className="flex items-center gap-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                              >
                                <FileText className="w-4 h-4" /> Principal Comments
                              </button>
                            </div>
                          </>
                        ) : 'N/A'}
                      </td>
                      <td className="py-3 px-4">
                        {result.third_term ? (
                          <>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedResult(result?.third_term);
                                  setIsSubjectModalOpen(true);
                                }}
                                className="text-blue-600 hover:text-blue-800 text-xs"
                              >
                                Subjects
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedResult(result?.third_term);
                                  setIsPsychoModalOpen(true);
                                }}
                                className="text-green-600 hover:text-green-800 text-xs"
                              >
                                Psychomotor
                              </button>
                              <button
                                onClick={() => handleCheckResult(result, 'third')}
                                className="text-purple-600 hover:text-purple-800 text-xs"
                              >
                                {result.third_term?.checked ? 'View Result' : 'Check Result'}
                              </button>
                            </div>
                            <div className="flex flex-wrap gap-2 mt-2 text-xs">
                              <button
                                onClick={() => openCommentModal(result.third_term, 'form_teacher', 'Third Term', result.session, result.class_name)}
                                className="flex items-center gap-1 text-orange-600 hover:text-orange-800"
                              >
                                <MessageSquare className="w-4 h-4" /> Form Teacher Comments
                              </button>
                              <button
                                onClick={() => openCommentModal(result.third_term, 'principal', 'Third Term', result.session, result.class_name)}
                                className="flex items-center gap-1 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
                              >
                                <FileText className="w-4 h-4" /> Principal Comments
                              </button>
                            </div>
                          </>
                        ) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No results found</p>
            </div>
          )}
        </div>
      </div>

      {/* Student Info Modal */}
      {isInfoModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Student Information</h2>
            <div className="space-y-2">
              <p><strong>Name:</strong> {userInfo?.firstname} {userInfo?.middlename} {userInfo?.lastname}</p>
              <p><strong>Admission No:</strong> {userInfo?.admission_no || 'N/A'}</p>
              <p><strong>Email:</strong> {userInfo?.student_email || 'N/A'}</p>
              <p><strong>Phone:</strong> {userInfo?.student_phone || 'N/A'}</p>
              <p><strong>Gender:</strong> {userInfo?.gender || 'N/A'}</p>
              <p><strong>Date of Birth:</strong> {userInfo?.date_of_birth || 'N/A'}</p>
              <p><strong>Blood Group:</strong> {userInfo?.blood_group || 'N/A'}</p>
              <p><strong>Religion:</strong> {userInfo?.religion || 'N/A'}</p>
              <p><strong>Address:</strong> {userInfo?.address || 'N/A'}</p>
              <p><strong>Parent Name:</strong> {userInfo?.parent_name || 'N/A'}</p>
              <p><strong>Parent Phone:</strong> {userInfo?.parent_phone || 'N/A'}</p>
              <p><strong>Parent Email:</strong> {userInfo?.parent_email || 'N/A'}</p>
              <p><strong>Emergency Phone:</strong> {userInfo?.emergency_phone || 'N/A'}</p>
              <p><strong>State of Origin:</strong> {userInfo?.state_of_origin || 'N/A'}</p>
              <p><strong>LGA:</strong> {userInfo?.lga || 'N/A'}</p>
              <p><strong>Nationality:</strong> {userInfo?.nationality || 'N/A'}</p>
              <p><strong>Allergies:</strong> {userInfo?.allergies || 'N/A'}</p>
              <p><strong>Special Needs:</strong> {userInfo?.special_needs || 'N/A'}</p>
            </div>
            <button
              onClick={() => setIsInfoModalOpen(false)}
              className="mt-4 w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Subject Scores Modal */}
      {isSubjectModalOpen && selectedResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setIsSubjectModalOpen(false)} // click outside closes
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Subject Scores
            </h2>

            <table className="w-full text-left">
              <thead className="sticky top-0 bg-white dark:bg-gray-800">
                <tr className="text-sm text-gray-600 dark:text-gray-400">
                  <th className="py-2 px-3">Subject</th>
                  <th className="py-2 px-3">Test 1</th>
                  <th className="py-2 px-3">Test 2</th>
                  <th className="py-2 px-3">Assignment</th>
                  <th className="py-2 px-3">Project</th>
                  <th className="py-2 px-3">Exam</th>
                  <th className="py-2 px-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {selectedResult.subjects?.map((subject, index) => (
                  <tr
                    key={index}
                    className="border-t border-gray-200 dark:border-gray-700"
                  >
                    <td className="py-2 px-3">{subject.subject_name}</td>
                    <td className="py-2 px-3">{subject?.test1 ? formatScore(subject.test1) : '-'}</td>
                    <td className="py-2 px-3">{subject?.test2 ? formatScore(subject.test2) : '-'}</td>
                    <td className="py-2 px-3">{subject?.assignment ? formatScore(subject.assignment) : '-'}</td>
                    <td className="py-2 px-3">{subject?.project ? formatScore(subject.project) : '-'}</td>
                    <td className="py-2 px-3">{subject?.exam ? formatScore(subject.exam) : '-'}</td>
                    <td className="py-2 px-3">{subject?.total_score ? formatScore(subject.total_score) : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <button
              onClick={() => setIsSubjectModalOpen(false)}
              className="mt-4 w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Psychomotor Skills Modal */}
      {isPsychoModalOpen && selectedResult && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setIsPsychoModalOpen(false)} // close when clicking outside
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
          >
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              Psychomotor Skills
            </h2>

            {/* Grid layout for pairs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
              {Object.entries(selectedResult?.psychomotors || {}).map(([key, value]) => (
                <p key={key}>
                  <strong>
                    {key
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, c => c.toUpperCase())}
                    :
                  </strong>{' '}
                  {value ?? 'N/A'}
                </p>
              ))}
            </div>

            <button
              onClick={() => setIsPsychoModalOpen(false)}
              className="mt-4 w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Comment Modal */}
      {commentModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setCommentModal(prev => ({ ...prev, open: false }))}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {commentModal.role} Comment
              </h2>
              <button
                onClick={() => setCommentModal(prev => ({ ...prev, open: false }))}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
              >
                ✕
              </button>
            </div>
            <div className="space-y-2 text-sm text-gray-800 dark:text-gray-200">
              <p><strong>Term:</strong> {commentModal.term}</p>
              <p><strong>Session:</strong> {commentModal.session}</p>
              <p><strong>Class:</strong> {commentModal.className}</p>
              {commentModal.date && (
                <p><strong>Date:</strong> {new Date(commentModal.date).toLocaleString()}</p>
              )}
              <div className="mt-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700">
                {commentModal.comment}
              </div>
            </div>
            <button
              onClick={() => setCommentModal(prev => ({ ...prev, open: false }))}
              className="mt-4 w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {isChatModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-sm">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Send Message</h2>
            <select
              value={chatRecipient}
              onChange={(e) => setChatRecipient(e.target.value)}
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white mb-2"
            >
              <option value="">Select Recipient</option>
              <option value="form_teacher">Form Teacher</option>
              <option value="school_admin">School Admin</option>
            </select>
            <textarea
              value={chatMessage}
              onChange={(e) => setChatMessage(e.target.value)}
              placeholder="Type your message..."
              className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white h-32"
            />
            <div className="flex space-x-2 mt-4">
              <button
                onClick={handleSendMessage}
                className="flex-1 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                disabled={!chatRecipient || !chatMessage}
              >
                Send
              </button>
              <button
                onClick={() => setIsChatModalOpen(false)}
                className="flex-1 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={() => setIsChatModalOpen(true)}
        className="fixed bottom-4 right-4 p-4 bg-green-600 text-white rounded-full shadow-lg hover:bg-green-700"
      >
        <MessageSquare className="w-6 h-6" />
      </button>
    </div>
  );
};

export default StudentDashboard;